import React, { useState, useMemo } from "react";
import {
  FiMapPin,
  FiCamera,
  FiEdit,
  FiTrash2,
  FiPlus,
  FiBarChart2,
  FiTrendingUp,
  FiAlertCircle,
  FiCheckCircle,
  FiLayers,
} from "react-icons/fi";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  Legend,
} from "recharts";
import { useTheme } from "../../../context/ThemeContext";
import SiteModal from "../SiteModal";

export default function SitesTab({
  org,
  sites: sitesProp,
  currentUser,
  onSelectSite,
  onAddSite,
  onEditSite,
  onDeleteSite,
}) {
  const { theme } = useTheme();
  const [showSiteModal, setShowSiteModal] = useState(false);
  const [editingSite, setEditingSite] = useState(null);
  const [mode, setMode] = useState("add");

  /* ---------------------------------------------------
     SOURCE OF TRUTH FOR SITES
     --------------------------------------------------- */
  const sites = useMemo(() => {
    if (Array.isArray(sitesProp) && sitesProp.length > 0) {
      return sitesProp;
    }
    return org?.sites || [];
  }, [sitesProp, org]);

  /* ---------------------------------------------------
     Permissions
     --------------------------------------------------- */
  const memberships = currentUser?.memberships || [];
  const orgMembership = memberships.find(
    (m) => m.organization?.id === org.id
  );

  const orgRole = orgMembership?.role || "";
  const globalRole = currentUser?.global_role || "";
  const canManage = globalRole === "platform_admin" || orgRole === "org_admin";

  /* ---------------------------------------------------
     Summary Stats
     --------------------------------------------------- */
  const summary = useMemo(() => {
    let totalCameras = 0;
    let totalOnline = 0;

    const chartData = sites.map((s) => {
      const cameras = s.camera_count || 0;
      const online =
        s.cameras?.filter((c) => c.connection_status === "online")?.length || 0;

      totalCameras += cameras;
      totalOnline += online;

      return {
        name: s.name.length > 12 ? s.name.slice(0, 12) + "…" : s.name,
        online,
        offline: cameras - online,
      };
    });

    return {
      totalSites: sites.length,
      totalCameras,
      totalOnline,
      totalOffline: totalCameras - totalOnline,
      uptimePct:
        totalCameras > 0
          ? ((totalOnline / totalCameras) * 100).toFixed(1)
          : 0,
      chartData,
    };
  }, [sites]);

  /* ---------------------------------------------------
     Theme-aware Styles
     --------------------------------------------------- */
  const card =
    theme === "dark"
      ? "bg-neutral-750 border border-neutral-700"
      : "bg-white border border-gray-200";

  const textPrimary = theme === "dark" ? "text-gray-100" : "text-gray-900";
  const textSecondary = theme === "dark" ? "text-gray-400" : "text-gray-500";
  const hoverCard = theme === "dark" ? "hover:border-neutral-600" : "hover:border-gray-300";

  /* ---------------------------------------------------
     Render
     --------------------------------------------------- */
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className={`text-3xl font-bold ${textPrimary}`}>Sites</h1>
          <p className={`${textSecondary} text-sm mt-1`}>
            Manage locations in{" "}
            <span className="text-blue-500 font-semibold">{org?.name}</span>
          </p>
        </div>

        {canManage && (
          <button
            onClick={() => {
              setEditingSite(null);
              setMode("add");
              setShowSiteModal(true);
            }}
            className="flex items-center gap-2 px-5 py-2.5 rounded-lg
                       bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium
                       shadow-lg hover:shadow-xl transition-all duration-200"
          >
            <FiPlus size={18} /> Add Site
          </button>
        )}
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          icon={<FiLayers />}
          label="Total Sites"
          value={summary.totalSites}
          iconColor="text-blue-500"
          iconBg={theme === "dark" ? "bg-blue-500/10" : "bg-blue-50"}
          theme={theme}
        />
        <StatCard
          icon={<FiCamera />}
          label="Total Cameras"
          value={summary.totalCameras}
          iconColor="text-purple-500"
          iconBg={theme === "dark" ? "bg-purple-500/10" : "bg-purple-50"}
          theme={theme}
        />
        <StatCard
          icon={<FiCheckCircle />}
          label="Online"
          value={summary.totalOnline}
          iconColor="text-green-500"
          iconBg={theme === "dark" ? "bg-green-500/10" : "bg-green-50"}
          theme={theme}
        />
        <StatCard
          icon={<FiAlertCircle />}
          label="Offline"
          value={summary.totalOffline}
          iconColor="text-red-500"
          iconBg={theme === "dark" ? "bg-red-500/10" : "bg-red-50"}
          theme={theme}
        />
      </div>

      {/* Sites Grid */}
      {sites.length === 0 ? (
        <EmptyState canManage={canManage} onAdd={() => {
          setEditingSite(null);
          setMode("add");
          setShowSiteModal(true);
        }} theme={theme} />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
          {sites.map((site) => (
            <SiteCard
              key={site.id}
              site={site}
              org={org}
              canManage={canManage}
              onSelectSite={onSelectSite}
              onEdit={() => {
                setEditingSite(site);
                setMode("edit");
                setShowSiteModal(true);
              }}
              onDelete={() => onDeleteSite(org.id, site.id)}
              theme={theme}
              card={card}
              hoverCard={hoverCard}
              textPrimary={textPrimary}
              textSecondary={textSecondary}
            />
          ))}
        </div>
      )}

      {/* Health Chart */}
      {summary.chartData.length > 0 && (
        <div className={`${card} p-6 rounded-xl shadow-sm`}>
          <div className="flex items-center gap-3 mb-6">
            <div className={`p-2.5 rounded-lg ${
              theme === "dark" ? "bg-blue-500/10" : "bg-blue-50"
            }`}>
              <FiBarChart2 className="text-blue-500" size={20} />
            </div>
            <div>
              <h2 className={`text-lg font-semibold ${textPrimary}`}>
                Camera Health Overview
              </h2>
              <p className={`text-xs ${textSecondary}`}>
                Online vs Offline cameras across all sites
              </p>
            </div>
          </div>

          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={summary.chartData}>
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke={theme === "dark" ? "#374151" : "#e5e7eb"}
                  strokeOpacity={0.5}
                />
                <XAxis
                  dataKey="name"
                  tick={{ fill: theme === "dark" ? "#9ca3af" : "#6b7280", fontSize: 12 }}
                />
                <YAxis
                  tick={{ fill: theme === "dark" ? "#9ca3af" : "#6b7280", fontSize: 12 }}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: theme === "dark" ? "#1f2937" : "#ffffff",
                    border: `1px solid ${theme === "dark" ? "#374151" : "#e5e7eb"}`,
                    borderRadius: "8px",
                    color: theme === "dark" ? "#f3f4f6" : "#111827",
                  }}
                />
                <Legend
                  wrapperStyle={{
                    paddingTop: "20px",
                  }}
                />
                <Bar
                  dataKey="online"
                  stackId="a"
                  fill="#3b82f6"
                  radius={[4, 4, 0, 0]}
                  name="Online"
                />
                <Bar
                  dataKey="offline"
                  stackId="a"
                  fill="#8b5cf6"
                  radius={[4, 4, 0, 0]}
                  name="Offline"
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Site Modal */}
      {canManage && (
        <SiteModal
          open={showSiteModal}
          onClose={() => {
            setShowSiteModal(false);
            setEditingSite(null);
            setMode("add");
          }}
          mode={mode}
          initialSite={editingSite}
          onSubmit={async (payload) => {
            if (mode === "edit" && editingSite?.id) {
              await onEditSite(org.id, { ...payload, id: editingSite.id });
            } else {
              await onAddSite(org.id, payload);
            }
            setShowSiteModal(false);
            setEditingSite(null);
            setMode("add");
          }}
          dark={theme === "dark"}
        />
      )}
    </div>
  );
}

/* ---------------------------------------------------
   Stat Card Component
   --------------------------------------------------- */
function StatCard({ icon, label, value, iconColor, iconBg, theme }) {
  const card = theme === "dark"
    ? "bg-neutral-750 border border-neutral-700"
    : "bg-white border border-gray-200";
  const textPrimary = theme === "dark" ? "text-gray-100" : "text-gray-900";
  const textSecondary = theme === "dark" ? "text-gray-400" : "text-gray-500";

  return (
    <div className={`${card} p-5 rounded-xl shadow-sm hover:shadow-md transition-shadow`}>
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <p className={`text-sm ${textSecondary} mb-1`}>{label}</p>
          <p className={`text-3xl font-bold ${textPrimary}`}>{value}</p>
        </div>
        <div className={`p-3 rounded-lg ${iconBg}`}>
          <div className={iconColor}>
            {React.cloneElement(icon, { size: 24 })}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ---------------------------------------------------
   Site Card Component
   --------------------------------------------------- */
function SiteCard({
  site,
  org,
  canManage,
  onSelectSite,
  onEdit,
  onDelete,
  theme,
  card,
  hoverCard,
  textPrimary,
  textSecondary,
}) {
  const [isHovered, setIsHovered] = useState(false);

  const cameraCount = site.camera_count || 0;
  const onlineCount = site.cameras?.filter((c) => c.connection_status === "online")?.length || 0;
  const healthPercent = cameraCount > 0 ? Math.round((onlineCount / cameraCount) * 100) : 0;

  return (
    <div
      className={`${card} ${hoverCard} rounded-xl overflow-hidden shadow-sm hover:shadow-lg transition-all duration-200 cursor-pointer`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={() => onSelectSite?.(org.id, site.id)}
    >
      {/* Header with Gradient */}
      <div className="relative h-28 bg-gradient-to-br from-blue-500 via-blue-600 to-cyan-500 flex items-center justify-center">
        <FiMapPin className="text-white text-4xl opacity-90" />
        
        {/* Health Badge */}
        {cameraCount > 0 && (
          <div className="absolute top-3 left-3 px-2.5 py-1 rounded-full bg-white/20 backdrop-blur-sm">
            <span className="text-white text-xs font-semibold">
              {healthPercent}% Online
            </span>
          </div>
        )}

        {/* Action Buttons */}
        {canManage && (
          <div
            className={`absolute top-3 right-3 flex gap-2 transition-opacity duration-200 ${
              isHovered ? "opacity-100" : "opacity-0"
            }`}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={onEdit}
              className="p-2 rounded-lg bg-white/90 hover:bg-white text-gray-700 hover:text-blue-600 transition-colors"
              title="Edit site"
            >
              <FiEdit size={16} />
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                onDelete();
              }}
              className="p-2 rounded-lg bg-white/90 hover:bg-white text-gray-700 hover:text-red-600 transition-colors"
              title="Delete site"
            >
              <FiTrash2 size={16} />
            </button>
          </div>
        )}
      </div>

      {/* Content */}
      <div className="p-5 space-y-3">
        <div>
          <h3 className={`text-lg font-semibold ${textPrimary} truncate`}>
            {site.name}
          </h3>
          <p className={`text-xs ${textSecondary} truncate mt-0.5`}>
            {site.address || "No address specified"}
          </p>
        </div>

        {/* Stats */}
        <div className="flex items-center justify-between pt-2 border-t border-gray-200 dark:border-neutral-700">
          <div className="flex items-center gap-2">
            <FiCamera className={textSecondary} size={16} />
            <span className={`text-sm ${textSecondary}`}>
              {cameraCount} camera{cameraCount !== 1 ? 's' : ''}
            </span>
          </div>
          
          {cameraCount > 0 && (
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-1">
                <div className="w-2 h-2 rounded-full bg-green-500"></div>
                <span className="text-xs text-green-600 dark:text-green-400 font-medium">
                  {onlineCount}
                </span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-2 h-2 rounded-full bg-red-500"></div>
                <span className="text-xs text-red-600 dark:text-red-400 font-medium">
                  {cameraCount - onlineCount}
                </span>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ---------------------------------------------------
   Empty State Component
   --------------------------------------------------- */
function EmptyState({ canManage, onAdd, theme }) {
  const card = theme === "dark"
    ? "bg-neutral-750 border border-neutral-700"
    : "bg-white border border-gray-200";
  const textPrimary = theme === "dark" ? "text-gray-100" : "text-gray-900";
  const textSecondary = theme === "dark" ? "text-gray-400" : "text-gray-500";

  return (
    <div className={`${card} rounded-xl p-12 text-center shadow-sm`}>
      <div className={`inline-flex p-4 rounded-full mb-4 ${
        theme === "dark" ? "bg-blue-500/10" : "bg-blue-50"
      }`}>
        <FiMapPin className="text-blue-500 text-4xl" />
      </div>
      <h3 className={`text-xl font-semibold ${textPrimary} mb-2`}>
        No sites yet
      </h3>
      <p className={`${textSecondary} mb-6 max-w-md mx-auto`}>
        {canManage
          ? "Get started by creating your first site. Sites help organize cameras by location."
          : "No sites have been created for this organization yet."}
      </p>
      {canManage && (
        <button
          onClick={onAdd}
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg
                     bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium
                     shadow-lg hover:shadow-xl transition-all duration-200"
        >
          <FiPlus size={18} /> Create First Site
        </button>
      )}
    </div>
  );
}
